import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import { Button, Container } from 'react-bootstrap'
import { debounce, isBottomReached } from '@shared-submodule/utils'
import { useRouter } from 'next/router'
import { useLazyQuery } from '@apollo/client'

import { PLAYER_SEARCH } from '@shared-submodule/sm-graphql/players/players.query'
const PlayersFilter = dynamic(() => import('@shared/components/player/playersFilter'))
const PlayerCard = dynamic(() => import('@shared/components/player/playersList/playerCard'))
const NoData = dynamic(() => import('@shared/components/noData'))

export default function PlayersList({ allTeams, filteredTeams, isFiltered, totalCount }) {
  const router = useRouter()

  const payload = useRef({ ...router?.query, nLimit: 18, nSkip: 1 })
  const isAllTeam = useRef(!filteredTeams?.length && !isFiltered)
  const [totalPlayerCount, setTotalPlayerCount] = useState(totalCount)
  const [teamList, setTeamList] = useState(isAllTeam.current ? allTeams : filteredTeams)
  const btnDisabled = useRef(false)

  const [setFilters] = useLazyQuery(PLAYER_SEARCH, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const lastItem = data?.filterPlayerDetails?.aPlayerDetails?.[data?.filterPlayerDetails?.aPlayerDetails?.length - 1]
      const id = `${lastItem?._id}`
      if (btnDisabled.current) {
        isBottomReached(id, isReached)
      }
      if (payload.current.nSkip > 1) {
        setTeamList(prev => [...prev, ...(data?.filterPlayerDetails?.aPlayerDetails || [])])
        setTotalPlayerCount(data?.filterPlayerDetails?.nTotalCount)
      } else {
        btnDisabled.current = false
        setTeamList(data?.filterPlayerDetails?.aPlayerDetails)
        setTotalPlayerCount(data?.filterPlayerDetails?.nTotalCount)
      }
    }
  })

  async function isReached(reach) {
    if (reach) {
      payload.current.nSkip += 1
      if (payload?.current?.international === 'true') {
        payload.current.international = true
      } else if (payload?.current?.international === 'false') {
        delete payload.current.international
        delete router.query.international
      }
      if (payload?.current?.activePlayers === 'true') {
        payload.current.activePlayers = true
      } else if (payload?.current?.activePlayers === 'false') {
        delete payload.current.activePlayers
        delete router.query.activePlayers
      }
      setFilters({ variables: { input: payload.current } })
    }
  }

  function handleFilterChange(filter, type) {
    btnDisabled.current = false
    payload.current.nSkip = 1
    let newFilter
    if (type === 'clear') {
      payload.current = { startsWith: 'A', nSkip: 1, nLimit: 18 }
      router.push({ query: { startsWith: 'A' } }, null, { shallow: true })
      isAllTeam.current = true
      setFilters({ variables: { input: payload.current } })
    } else {
      if (type === 'startsWith') {
        delete router?.query?.sSearch
        delete payload?.current?.sSearch
      }
      if (typeof filter !== 'object') {
        newFilter = filter
      } else {
        newFilter = filter?.sLabel
      }
      if (payload?.current?.international === 'true') {
        payload.current.international = true
      } else if (payload?.current?.international === 'false') {
        delete payload.current.international
        delete router.query.international
      }
      if (payload?.current?.activePlayers === 'true') {
        payload.current.activePlayers = true
      } else if (payload?.current?.activePlayers === 'false') {
        delete payload.current.activePlayers
        delete router.query.activePlayers
      }
      if (!payload.current.sSearch && !payload.current.startsWith) {
        payload.current.startsWith = 'A'
        router.query.startsWith = 'A'
      }
      payload.current.nLimit = 18
      payload.current[type] = newFilter
      isAllTeam.current = false
      setFilters({ variables: { input: payload.current } })
      router.push({ query: { ...router?.query, [type]: newFilter } }, null, { shallow: true })
    }
  }
  const handleLoadMore = () => {
    payload.current.nSkip += 1
    btnDisabled.current = true
    if (!payload?.current?.startsWith && !payload?.current?.sSearch) {
      payload.current.startsWith = 'A'
    }
    if (payload?.current?.international === 'true') {
      payload.current.international = true
    } else if (payload?.current?.international === 'false') {
      delete payload.current.international
      delete router.query.international
    }
    if (payload?.current?.activePlayers === 'true') {
      payload.current.activePlayers = true
    } else if (payload?.current?.activePlayers === 'false') {
      delete payload.current.activePlayers
      delete router.query.activePlayers
    }
    setFilters({ variables: { input: { ...payload.current } } })
  }

  const handleSearchChange = debounce(function handleSearchChange(sSearch) {
    payload.current.nLimit = 18
    if (sSearch) {
      payload.current.nSkip = 1
      payload.current.sSearch = sSearch
      isAllTeam.current = false
      delete router?.query?.startsWith
      delete payload?.current?.startsWith
      if (payload?.current?.international === 'true') {
        payload.current.international = true
      } else if (payload?.current?.international === 'false') {
        delete payload.current.international
        delete router.query.international
      }
      if (payload?.current?.activePlayers === 'true') {
        payload.current.activePlayers = true
      } else if (payload?.current?.activePlayers === 'false') {
        delete payload.current.activePlayers
        delete router.query.activePlayers
      }
      setFilters({ variables: { input: payload.current } })
      router.push({ query: { ...router?.query, sSearch } }, null, { shallow: true })
    } else {
      delete payload.current.sSearch
      const query = { ...router?.query }
      delete query?.sSearch
      query.startsWith = 'A'
      payload.current.startsWith = 'A'
      if (payload?.current?.international === 'true') {
        payload.current.international = true
      } else if (payload?.current?.international === 'false') {
        delete payload.current.international
        delete query.international
      }
      if (payload?.current?.activePlayers === 'true') {
        payload.current.activePlayers = true
      } else if (payload?.current?.activePlayers === 'false') {
        delete payload.current.activePlayers
        delete query.activePlayers
      }
      isAllTeam.current = true
      setFilters({ variables: { input: payload.current } })
      router.push({ query }, null, { shallow: true })
    }
  })

  return (
    <>
      <PlayersFilter defaultValues={router?.query} handleFilterChange={handleFilterChange} handleSearchChange={handleSearchChange} />
      <div className='common-box border-0 rounded-0 mb-0 pb-0 bg-playerCard'>
        <Container className=''>
          {!teamList?.length ? <div className='pb-3'>
            <NoData title='No numbers yet—maybe they’re busy planning something legendary!'/>
          </div> : <PlayerCard teamsList={teamList} />}
        </Container>
      </div>
      {totalPlayerCount > teamList?.length && <div className="text-center bg-playerCard pb-3">
        <Button className="theme-btn" onClick={handleLoadMore}>{'Load more'}</Button>
      </div>}

    </>
  )
}

PlayersList.propTypes = {
  allTeams: PropTypes.array,
  filteredTeams: PropTypes.array,
  isFiltered: PropTypes.bool,
  totalCount: PropTypes.number
}
